// The primitives every mechanic is built from. Each helper returns a new
// state and leaves the input alone. Mechanics speak through `say`, which
// looks the line up on the card and falls back to the spec.

import { EVENT_HISTORY, FIRST_TURN, MOVED_STOP_TURNS, STOPPED_PENALTY_RATIO } from '../constants';
import { CARDS } from '../content/cards';
import { BASE_ABILITIES, comboName, hasComboName } from '../engine/abilities';
import { cloneOnSquare, promoteIfDue } from '../engine/apply';
import { describePiece, OWNER_NAMES } from '../engine/describe';
import { pieceValue } from '../engine/evaluate';
import { generateMoves, isCapture, isSquareAttacked, movesForPiece } from '../engine/moves';
import { createPiece, otherSide, withPiece } from '../engine/pieces';
import { squareName } from '../engine/squares';
import { isDeadly } from '../engine/terrain';
import { hasAnyBuffTrait, withoutBuffTraits } from '../engine/traits';
import type { Board, EnergyGain, Owner, Piece, PieceKind, Side } from '../engine/types';
import type { EventRecord, GameState, LogTone } from '../game/state';
import type { LineVars, SpellMechanic } from './types';

export { describePiece, OWNER_NAMES };

export const addLog = (state: GameState, tone: LogTone, text: string): GameState => ({
  ...state,
  log: [...state.log, { id: state.nextLogId, ply: state.ply, tone, text }],
  nextLogId: state.nextLogId + 1,
});

/**
 * Something the rules did. It goes in the log and the narrator plays it. With
 * no tone it is a timer: a condition that ran out, or a count that reached zero.
 */
export const recordEvent = (
  state: GameState,
  caption: string,
  text: string,
  squares: readonly number[],
  tone: LogTone = 'timer',
  aside: readonly number[] = [],
  energy?: EnergyGain,
): GameState => {
  const event: EventRecord = {
    serial: state.nextEventSerial,
    caption,
    text,
    squares,
    aside,
    tone,
    ...(energy ? { energy } : {}),
  };
  return {
    ...addLog(state, tone, text),
    events: [...state.events, event].slice(-EVENT_HISTORY),
    nextEventSerial: state.nextEventSerial + 1,
  };
};

// ---- Lines ---------------------------------------------------------------

/** Fills {vars} in a template. An unknown var stays as written, so it shows. */
export const renderLine = (template: string, vars: LineVars): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = vars[key];
    return value === undefined ? match : String(value);
  });

/** The card's line for the key when it has one, else the spec's. A blank line is not written yet. */
export const lineFor = (mechanic: SpellMechanic, key: string, vars: LineVars = {}): string => {
  const template = CARDS[mechanic.id]?.lines?.[key] || mechanic.spec.lines[key];
  if (template === undefined) throw new Error(`${mechanic.id} has no line "${key}"`);
  return renderLine(template, vars);
};

/** Writes the mechanic's line to the log in the polarity's tone. */
export const say = (
  state: GameState,
  mechanic: SpellMechanic,
  key: string,
  vars: LineVars = {},
): GameState => addLog(state, mechanic.polarity, lineFor(mechanic, key, vars));

/**
 * The spell had nothing to do. The line is the mechanic's "noEffect" line,
 * or another key. The flow sees the flag and gives one free roll a turn.
 */
export const noEffect = (
  state: GameState,
  mechanic: SpellMechanic,
  key = 'noEffect',
  vars: LineVars = {},
): GameState => ({
  ...addLog(state, 'system', lineFor(mechanic, key, vars)),
  hadNoEffect: true,
});

/**
 * Picks "cast" or "castCombo" for a piece that just changed, and fills
 * {combo} with the combination's name. A mechanic that grants abilities
 * says `say(state, this, ...comboLine(piece), { who, ...vars })`.
 */
export const comboLine = (piece: Piece): [key: string, vars: LineVars] =>
  hasComboName(piece) ? ['castCombo', { combo: comboName(piece) }] : ['cast', {}];

// ---- Pieces ----------------------------------------------------------------

export const pieceAt = (state: GameState, square: number): Piece => {
  const piece = state.board[square];
  if (!piece) throw new Error(`No piece on ${squareName(square)}`);
  return piece;
};

export const updatePiece = (
  state: GameState,
  square: number,
  patch: Partial<Piece>,
): GameState => ({
  ...state,
  board: withPiece(state.board, square, { ...pieceAt(state, square), ...patch }),
});

export const placeNewPiece = (
  state: GameState,
  square: number,
  owner: Owner,
  kind: PieceKind,
): GameState => ({
  ...state,
  board: withPiece(state.board, square, createPiece(state.nextPieceId, owner, kind, square)),
  nextPieceId: state.nextPieceId + 1,
});

/** Relocates a piece to an empty square. Promotes a pawn that lands far. */
export const relocatePiece = (board: Board, from: number, to: number): Board => {
  const piece = board[from];
  if (!piece) throw new Error(`No piece on ${squareName(from)}`);
  const cleared = withPiece(board, from, null);
  return withPiece(cleared, to, promoteIfDue({ ...piece, moved: true }, to));
};

/**
 * A spell put a piece on the square. The terrain there does what it does
 * after a move: a deadly square removes the piece, and a clone square copies
 * it. Call it after `stopMoved`, so the copy is stopped too.
 */
export const settleLanding = (state: GameState, square: number): GameState => {
  const piece = state.board[square];
  if (piece && isDeadly(state.rules, square)) {
    const gone: GameState = {
      ...state,
      board: withPiece(state.board, square, null),
      captured: [...state.captured, piece],
    };
    const text = `${describePiece(piece, square)} landed on deadly square. Gone.`;
    return recordEvent(gone, 'Deadly square', text, [square], 'capture');
  }
  const cloned = cloneOnSquare(state.board, state.rules, square);
  if (!cloned) return state;
  const { spot, note } = cloned;
  const copy = spot === null ? null : cloned.board[spot];
  const numbered: GameState =
    spot === null || !copy
      ? state
      : {
          ...state,
          board: withPiece(cloned.board, spot, { ...copy, id: state.nextPieceId }),
          nextPieceId: state.nextPieceId + 1,
        };
  return recordEvent(numbered, note.caption, note.text, note.squares, 'capture');
};

export const swapPieces = (board: Board, a: number, b: number): Board => {
  const next = [...board];
  const pieceA = next[a] ?? null;
  next[a] = next[b] ?? null;
  next[b] = pieceA;
  return next;
};

/**
 * A piece that a boardwide spell moved cannot move for a turn, so a pawn that lands beside
 * a king cannot take it before the owner can react. Every mechanic that
 * relocates pieces without the owner's say uses this.
 */
export const stopMoved = (
  board: Board,
  squares: readonly number[],
  turns: number = MOVED_STOP_TURNS,
): Board =>
  board.map((piece, square) =>
    piece && squares.includes(square) && piece.owner !== 'neutral'
      ? { ...piece, stoppedTurns: Math.max(piece.stoppedTurns, turns) }
      : piece,
  );

/**
 * Everything a spell gave. Debuffs on the piece stay, and so does a crown: if
 * a crowned pawn is the side's only royal piece, stripping it ends the match.
 */
export const forgetGains = (piece: Piece): Piece => ({
  ...withoutBuffTraits(piece),
  abilities: BASE_ABILITIES[piece.kind],
  gained: [],
  learnedFrom: {},
  lives: 1,
});

export const hasGains = (piece: Piece): boolean =>
  piece.gained.length > 0 || piece.lives > 1 || hasAnyBuffTrait(piece);

// ---- Queries ---------------------------------------------------------------

export const squaresWhere = (
  state: GameState,
  predicate: (piece: Piece, square: number) => boolean,
): number[] => {
  const result: number[] = [];
  state.board.forEach((piece, square) => {
    if (piece && predicate(piece, square)) result.push(square);
  });
  return result;
};

export const ownPieces = (state: GameState, side: Side, extra?: (piece: Piece) => boolean) =>
  squaresWhere(state, (piece) => piece.owner === side && (extra ? extra(piece) : true));

export const enemyPieces = (state: GameState, side: Side, extra?: (piece: Piece) => boolean) =>
  squaresWhere(
    state,
    (piece) => piece.owner !== side && piece.owner !== 'neutral' && (extra ? extra(piece) : true),
  );

export const enemyOrNeutralPieces = (
  state: GameState,
  side: Side,
  extra?: (piece: Piece) => boolean,
) => squaresWhere(state, (piece) => piece.owner !== side && (extra ? extra(piece) : true));

const positionOf = (state: GameState) => ({ board: state.board, rules: state.rules });

export const canMoveNow = (state: GameState, square: number): boolean => {
  const piece = state.board[square];
  if (!piece || piece.owner === 'neutral') return false;
  return generateMoves(positionOf(state), piece.owner).some((move) => move.from === square);
};

/** Is a piece of any side about to explode? */
export const isFuseLit = (board: Board): boolean =>
  board.some((piece) => piece !== null && piece.explodeIn > 0);

export const isSlider = (piece: Piece): boolean =>
  piece.abilities.includes('bishop') ||
  piece.abilities.includes('rook') ||
  piece.abilities.includes('cannon');

// ---- Scoring helpers for a mechanic's own AI opinion ----------------------

/** Could the enemy of `side` take the piece on the square next move? */
export const isThreatened = (state: GameState, square: number, side: Side): boolean =>
  isSquareAttacked(positionOf(state), square, otherSide(side));

/** Can `caster` take the enemy piece on the square with its move this turn? */
export const casterCanTake = (state: GameState, square: number, caster: Side): boolean =>
  isSquareAttacked(positionOf(state), square, caster);

/** The squares the piece on `from` can go to with its own move now. */
export const ownMoveSquares = (state: GameState, from: number): number[] =>
  movesForPiece(positionOf(state), from).map((move) => move.to);

export const valueAt = (state: GameState, square: number): number => {
  const piece = state.board[square];
  return piece ? pieceValue(piece) : 0;
};

/** The value of the best piece that the piece on the square could take now. */
export const bestCaptureValue = (state: GameState, square: number): number => {
  const position = positionOf(state);
  return movesForPiece(position, square)
    .filter((move) => isCapture(position, move))
    .reduce((best, move) => Math.max(best, valueAt(state, move.to)), 0);
};

/**
 * What the caster loses when a spell changes its piece on the square,
 * because the piece cannot move for the rest of the turn: the move. A piece
 * that cannot move now loses nothing.
 */
export const lostMoveCost = (state: GameState, square: number, caster: Side): number => {
  if (state.board[square]?.owner !== caster || !canMoveNow(state, square)) return 0;
  return valueAt(state, square) * STOPPED_PENALTY_RATIO;
};

/**
 * The lost move, and the piece itself when the enemy can take it next move:
 * it cannot leave the square. For a spell that does not protect the piece.
 */
export const castStopCost = (state: GameState, square: number, caster: Side): number => {
  const lostMove = lostMoveCost(state, square, caster);
  if (lostMove === 0) return 0;
  return lostMove + (isThreatened(state, square, caster) ? valueAt(state, square) : 0);
};

/** Larger than the value of any piece, so a royal piece comes first. */
export const ROYAL_PICK_BONUS = 100_000;
/** Points for making the piece on the square a target: high when it is already attacked. */
export const BAIT_BONUS = 500;
export const UNSAFE_PENALTY = 1000;
/** More than the ranks of a board and less than a piece, for a square the piece can go to with its own move. */
export const OWN_MOVE_PENALTY = 10;
/** Larger than the value of any piece, so a target the caster can take now comes last. */
export const TAKEN_SOON_PENALTY = 100_000;

// ---- Definition ------------------------------------------------------------

type MechanicDefaults =
  | 'targetCount'
  | 'eligible'
  | 'aiPreference'
  | 'canDeal'
  | 'canCast'
  | 'earliestTurn';

/** Fills in the fields every mechanic shares so each definition stays short. */
export const defineMechanic = (
  mechanic: Omit<SpellMechanic, MechanicDefaults> & Partial<Pick<SpellMechanic, MechanicDefaults>>,
): SpellMechanic => ({
  targetCount: 0,
  eligible: () => [],
  aiPreference: 'valuable',
  canDeal: () => true,
  canCast: () => true,
  earliestTurn: FIRST_TURN,
  ...mechanic,
});
